import React from 'react'
import { cn } from '@/lib/utils'
import { toSvgPath, type LinkPath } from '../../lib/link-routing'

export interface DependencyLinkProps {
  path: LinkPath
  critical: boolean
  selected: boolean
  hovered: boolean
  onClick?: (e: React.MouseEvent) => void
  onPointerEnter?: (e: React.PointerEvent) => void
  onPointerLeave?: () => void
}

const ARROW = 6

/** Orthogonal dependency polyline with an arrowhead and a wide transparent hit path. Spec B §8.5. */
const DependencyLink: React.FC<DependencyLinkProps> = ({ path, critical, selected, hovered, onClick, onPointerEnter, onPointerLeave }) => {
  const d = toSvgPath(path.points)
  // Fix round 1: the hit path uses `hitPoints`, not `points` — trimmed past a bar's own
  // resize/link handles on the ends that sit at its right edge (x2), so those handles stay
  // clickable even once the bar has an outgoing FS/FF or incoming FF/SF link (see link-routing.ts).
  const dHit = toSvgPath(path.hitPoints)
  const colour = selected ? 'stroke-primary' : critical ? 'stroke-destructive' : 'stroke-muted-foreground'
  const fillColour = selected ? 'fill-primary' : critical ? 'fill-destructive' : 'fill-muted-foreground'
  const strokeWidth = selected || hovered ? 2 : critical ? 1.5 : 1
  const { x, y, dir } = path.arrow
  const half = ARROW / 2
  const back = dir === 'right' ? x - ARROW : x + ARROW
  const arrowPoints = `${x},${y} ${back},${y - half} ${back},${y + half}`
  return (
    <g data-link onClick={onClick} className="cursor-pointer">
      {/* Fix round 1: the visible stroke and arrowhead are decorative only (`pointer-events-none`) —
          their own geometry still runs flush to a bar's edge (so the line looks connected), which
          would otherwise re-intercept clicks/hover at exactly the same spot the trimmed `hitPoints`
          were meant to clear (SVG hit-testing follows paint order, and this `<g>` paints after the
          bars). The invisible `gantt-link-hit` path below is the only interactive shape. */}
      <path d={d} className={cn('fill-none pointer-events-none', colour)} strokeWidth={strokeWidth} />
      <polygon points={arrowPoints} className={cn('pointer-events-none', fillColour)} />
      <path d={dHit} className="gantt-link-hit fill-none stroke-transparent" strokeWidth={6} onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave} />
    </g>
  )
}

export default DependencyLink
